import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { formatCurrencyValue } from '../../constants/chartConfig';
import { colors } from '../../utils/constants';

// Category colors for stacked view
const CATEGORY_COLORS = {
  efficiency: '#333790',  // Blue
  income: '#10B981',      // Green
  contract: '#F59E0B'     // Amber
};

const CompanyBreakdownChart = ({
  data,
  title,
  subtitle,
  viewMode = 'byCompany', // 'byCompany' or 'byCategory'
  showLegend = true
}) => {
  if (!data || data.length === 0) return null;

  // Calculate totals for percentage display
  const grandTotal = data.reduce((sum, company) => sum + company.total, 0);

  // Find the max value to determine the appropriate scale
  const maxValue = Math.max(...data.map(d => d.total));

  // Smart axis formatter based on data magnitude
  const formatAxisValue = (value) => {
    if (value === 0) return '$0';
    if (maxValue >= 1000000) {
      // Use millions for YTD-scale data
      return `$${(value / 1000000).toFixed(1)}M`;
    } else {
      // Use thousands for MTD/QTD-scale data
      return `$${Math.round(value / 1000)}K`;
    }
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const companyData = data.find(d => d.company === label);
      return (
        <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200">
          <p className="font-semibold text-gray-900 mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {formatCurrencyValue(entry.value)}
            </p>
          ))}
          <div className="border-t border-gray-200 mt-2 pt-2">
            <p className="text-sm font-medium text-gray-700">
              Total: {formatCurrencyValue(companyData?.total || 0)}
            </p>
            {companyData?.percentOfTotal && (
              <p className="text-xs text-gray-500">
                {companyData.percentOfTotal}% of portfolio
              </p>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white p-6 rounded-xl border shadow-sm">
      {/* Header */}
      <div className="mb-4">
        <h4 className="text-lg font-semibold text-gray-900">{title}</h4>
        {subtitle && <p className="text-sm text-gray-600 mt-1">{subtitle}</p>}
      </div>

      {/* Chart */}
      <div style={{ height: 500 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis
              type="number"
              tickFormatter={formatAxisValue}
              tick={{ fill: '#6B7280', fontSize: 11 }}
              tickCount={6}
            />
            <YAxis
              type="category"
              dataKey="company"
              tick={{ fill: '#6B7280', fontSize: 13 }}
              width={80}
            />
            <Tooltip content={<CustomTooltip />} />
            {showLegend && (
              <Legend
                verticalAlign="top"
                height={36}
                formatter={(value) => <span className="text-sm text-gray-700 capitalize">{value}</span>}
              />
            )}
            <Bar dataKey="efficiency" name="Efficiency" stackId="stack" fill={CATEGORY_COLORS.efficiency} />
            <Bar dataKey="income" name="Income" stackId="stack" fill={CATEGORY_COLORS.income} />
            <Bar dataKey="contract" name="Contract" stackId="stack" fill={CATEGORY_COLORS.contract} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Summary Stats */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Total Returns</span>
          <span className="text-lg font-semibold" style={{ color: colors.blue }}>
            {formatCurrencyValue(grandTotal)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default CompanyBreakdownChart;